import React, {useCallback, useEffect, useMemo} from 'react';
import {View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigation} from '@react-navigation/native';
import Screen from '../../shared/components/screen';
import {DeviceSize} from '../../shared/theme/helpers';
import {CustomScrollView} from '../../shared/components/scrollView';
import {Breadcrumbs} from '../../shared/components/breadcrumbs';
import {Routes, Screens} from '../../shared/routing';
import {Image} from '../../shared/components/image';
import {ContentWithRightImage} from '../../shared/components/contentWithRightImage';
import {ResponsiveView} from '../../shared/components/responsiveView';
import {Text} from '../../shared/components/text';
import {Markdown} from '../../shared/components/markdown';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {ECertificatesPng} from '../../assets/images';
import {contentSelector} from '../../redux/selectors/resources';
import {BreadcrumbItem} from '../../shared/components/breadcrumbs/model';
import {Tabs, TabsProps} from '../../shared/components/tabs';
import {activeCertificatesSelector, certificatesHistorySelector} from '../../redux/selectors/eCertificates';
import {getActiveCertificates, getCertificatesHistory} from '../../redux/actions/eCertificates';
import {useECertificatesStyles} from './ECertificates.style';
import {CertificateCard} from './components';

const routes: BreadcrumbItem[] = [
  {
    title: 'Возможности карты',
    routeName: Screens.CardServices,
    webRoute: Routes.cardService,
  },
  {
    title: 'Электронные сертификаты',
    routeName: Screens.ECertificates,
    webRoute: Routes.eCertificates,
  },
];

export function ECertificatesScreen() {
  const dispatch = useDispatch();
  const {navigate} = useNavigation();
  const styles = useECertificatesStyles();
  const isDesktop = useIsDesktop();

  const activeCertificates = useSelector(activeCertificatesSelector());
  const certificatesHistory = useSelector(certificatesHistorySelector());

  const content = useSelector(contentSelector());

  const handleCardPress = useCallback((id: number) => {
    navigate(Screens.CertificateItem, {id});
  }, []);

  const tabsItems = useMemo<TabsProps['items']>(
    () => [
      {
        key: 'active',
        label: 'Активные',
        isEmpty: activeCertificates === null || activeCertificates.length === 0,
        children: (
          <View>
            {activeCertificates?.map(({id, certificateType, certificateNumber, name, balance, endDate}) => (
              <CertificateCard
                id={id}
                certificateType={certificateType}
                key={id}
                name={name}
                balance={balance}
                certificateNumber={certificateNumber}
                endDate={endDate}
                onPress={handleCardPress}
                style={styles.card}
              />
            ))}
          </View>
        ),
      },
      {
        key: 'history',
        label: 'История',
        isEmpty: certificatesHistory === null || certificatesHistory.length === 0,
        children: (
          <View>
            {certificatesHistory?.map(({id, certificateType, certificateNumber, name, balance, endDate}) => (
              <CertificateCard
                id={id}
                certificateType={certificateType}
                key={id}
                name={name}
                balance={balance}
                certificateNumber={certificateNumber}
                endDate={endDate}
                onPress={handleCardPress}
                style={styles.card}
              />
            ))}
          </View>
        ),
      },
    ],
    [activeCertificates, certificatesHistory],
  );

  useEffect(() => {
    dispatch(getActiveCertificates());
    dispatch(getCertificatesHistory());
  }, []);

  return (
    <Screen fullWidth>
      <CustomScrollView
        contentInsetAdjustmentBehavior='automatic'
        withRefresh={true}
        onRefresh={() => {
          dispatch(getActiveCertificates());
          dispatch(getCertificatesHistory());
        }}>
        <View style={styles.root}>
          <View style={styles.topBlock}>
            <Breadcrumbs routes={routes} />
            <ContentWithRightImage
              icon={<Image source={ECertificatesPng as any} width={450} height={400} />}
              hideImage={!isDesktop}
              iconStyle={styles.programContentWithImageIcon}
              backgroundStyle={styles.programContentWithImageBackground}
              contentStyle={styles.programContentWithImageContent}>
              <ResponsiveView only={[DeviceSize.desktop]}>
                <Text style={styles.title} type='h2'>
                  {content.services.eCertificates.cardTitle}
                </Text>
                <View style={styles.contentTextWrapper}>
                  <Markdown listColumns={isDesktop ? 2 : 1}>
                    {content.services.eCertificates.descriptionMarkdown}
                  </Markdown>
                </View>
              </ResponsiveView>
            </ContentWithRightImage>
          </View>

          <View style={styles.bottomBlock}>
            <Tabs items={tabsItems} />
          </View>
        </View>
      </CustomScrollView>
    </Screen>
  );
}
